import { TBooleanInNumber, TTranslate } from './main';
import { BRANCH_DELIVERY_TYPE } from 'src/constants/deliveryTypes';
import { WORKING_HOURS_ORDER_TYPE } from 'src/view/screens/BranchesManagementScreen/screens/BranchEditScreen/types/branchEdit';
import { PAYMENT_TYPES } from '~/constants/main';

export interface IBranches {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
  from: number;
  to: number;
  data: [IBranch];
}

export interface IBranchPreparation {
  id: number;
  external_id: number | string;
  name: string;
  delivery: {
    unit: string; // m | h | d,
    time: number; // pickup time, always in minutes
    preparation_time: number; // always in minutes
    gap: number; // number of units
  };
  pickup: {
    unit: string; // m | h | d,
    time: number; // pickup time, always in minutes
    preparation_time: number; // always in minutes
    gap: number; // number of units
  };
  preorder: {
    interval: 1 | 5 | 15 | 30 | 60; // mins
    days: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7; // preorder latest date
  };
}

export interface IBranchEmenu {
  order_types: {
    is_dinein: boolean;
    is_togo: boolean;
  };
  pre_select_default: boolean;
  payment_types: [{ id: number; type: string; status: number; is_default: number }];
  sections: [{ id: number; name: string; tables: [{ id: number; name: string }] }];
}

export interface ISectionTable {
  id: number;
  name: TTranslate;
}

export interface IBranchWorkingTime {
  id: number;
  working_day: string;
  start_time: string;
  close_time: string;
  menu_group_id: number;
  menu_group_name: TTranslate;
  sort: number;
  order_type: WORKING_HOURS_ORDER_TYPE;
}

export enum BRANCH_DELIVERY_CONDITION {
  DISABLED,
  FIXED,
  BY_LOCATION,
}

export enum SETTINGS_DELIVERY_TYPE {
  REDUCED_DELIVERY_FEE,
  DELIVERY_FEE,
  MIN_ORDER,
}

export type IReduced = {
  id: number | null;
  delivery_fee: string | number;
  order_more_than: string | number;
};

export interface IBranchPaymentType {
  id: PAYMENT_TYPES;
  type: string;
}

export type IBranch = {
  working_time: IBranchWorkingTime[];
  default_payment_type: number;
  work_slots: IWorkSlot[];
  allow_all_payment_methods: TBooleanInNumber;
  confirmedAt?: number;
  preparation?: IBranchPreparation;
  delivery_paused?: string;

  id: number;
  external_id: number | string | null;
  name: TTranslate;
  is_delete: TBooleanInNumber;
  delivery_type: BRANCH_DELIVERY_TYPE; // p | d | b - order type
  preorder: TBooleanInNumber;

  address: string;
  phone: string;
  latitude: number;
  longitude: number;
  distance?: string;

  is_busy: boolean;
  status: TBooleanInNumber;
  available_payment_types: IBranchPaymentType[];
  payment_types: IBranchPaymentType[];
  pre_select_default: boolean;
  is_orders: number;
  is_curbside: number;
  is_pickup: boolean;

  delivery_fee: number | string;
  curbside_fee: number;
  delivery_fee_type: BRANCH_DELIVERY_CONDITION;
  min_order_type: BRANCH_DELIVERY_CONDITION;
  reduced_delivery_fee_type: BRANCH_DELIVERY_CONDITION;
  reduced: IReduced[];
  min_order: number | string;
  is_max_cash_payment: number; // 1 | 0
  is_max_cash_payment_hidden: number;
  max_cash_payment: number;
  payment_type: number;
  is_free_delivery: number;

  working_now: boolean;
  working_hours: string;
  closest_day: IBranchWorkingTime;
  city: {
    id: number;
    name: TTranslate;
  };
  is_emenu: boolean;
  emenu?: {
    qr?: {
      name: string;
      url: string;
      png: string;
    };
    order_types: {
      is_dinein: boolean;
      is_togo: boolean;
    };
    payment_types: [
      {
        id: PAYMENT_TYPES;
        is_default: boolean;
        type: string;
      }
    ];
    default_payment_type: number;
    sections: [
      {
        id: number;
        name: string;
        tables: ISectionTable[];
      }
    ];
  };
  sections?: {
    id: number;
    name: TTranslate;
    tables: ISectionTable[];
  }[];
  profile_id?: number;
  profile_name?: string;
  is_round_clock?: boolean;
  pos_service_id?: string | null;

  /**
   * Show cashback is available for branch
   *
   * @type {boolean}
   * @memberof IBranch
   */
  is_cashback?: boolean;
  /**
   * Show wallet is available for branch
   *
   * @type {boolean}
   */
  is_wallet?: boolean;

  currency?: {
    currency_code: string;
    currency_code_ar: string;
    currency_position?: string;
    divided: number;
  };
  /**
   * @deprecated
   * @type {string}
   */
  currency_code?: string;
  /**
   * @deprecated
   * @type {string}
   */
  currency_position?: string;

  vat_percent?: string;
  created_in: string;
  franchise_profile: string;
  branch_profile_id: number | null;
  branch_profile_name: string | null;

  areas?: {
    id: number;
    name: TTranslate;
    external_id: number | string;
    delivery_fee: number | string;
    min_order: number | string;
    free_delivery: number | string;
    reduced: IReduced[];
  }[];
};

export interface IWorkSlotBreak {
  start_time: string;
  end_time: string;
  time: string;
}
export interface IWorkSlot {
  day: string;
  breaks: null | IWorkSlotBreak[];
  start_time: string;
  close_time: string;
  is_full_day: boolean;
}

export enum UNAVAILABLE_BRANCHES_FLAG {
  ALL = 'all',
  DELETE = 'is_delete',
  STATUS = 'status',
}

/**
 * all === undefined in request
 * active === 1 for status, 0 for is_delete
 * inactive === 0 for status, 1 for is_delete
 *
 * @export
 * @enum {number}
 */
export enum BRANCHES_MODAL_FETCH_PARAMETER {
  ALL = 'all',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export interface IBranchFromSocket {
  id: number;
  name?: TTranslate;
  delivery_type?: IBranch['delivery_type'];
  is_curbside?: IBranch['is_curbside'];
  is_emenu?: IBranch['is_emenu'];
  payment_types?: IBranch['payment_types'];
  is_busy?: boolean; // 0 or 1
  status?: TBooleanInNumber; // 0 or 1
  is_delete?: TBooleanInNumber; // 0 or 1
  created_at?: string;
  updated_at?: {
    date?: string;
    timezone?: string;
    timezone_type?: number;
  };
}

export interface ICurrencySettings {
  currency_code?: string;
  currency_position?: string;
  currency_divided?: number;
}
